import { BankOperationStatusesNew } from 'constants/bankOperationStatusesNew';
import { Role } from 'constants/roles';

import { useMemo, useState } from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { usePermissions } from 'hooks/usePermissions';
import { useBulkApproveRequestsBankOps } from 'pages/Finance/RequestsBankOps/hooks/useBulkApproveRequstsBankOps';
import { useBulkDeleteRequestsBankOps } from 'pages/Finance/RequestsBankOps/hooks/useBulkDeleteRequestsBankOps';
import { useBulkOnApprovalRequestsBankOps } from 'pages/Finance/RequestsBankOps/hooks/useBulkOnApprovalRequstsBankOps';
import {
  BulkActionsToolbar,
  useListContext,
  useResourceContext,
  useUnselectAll,
} from 'react-admin';
import { Button, CancelButton } from 'shared/mui/Buttons';
import { BaseDialog } from 'shared/mui/Dialogs';
import { BankOperation } from 'types/embedded/bankOperation';
import { getId } from 'utils';

import { RequestsBankOpsListDatagridBulkRejectDialog } from './RequestsBankOpsListDatagridBulkRejectDialog';

export const RequestsBankOpsListDatagridBulk: React.FC = () => {
  const { data, selectedIds } = useListContext<BankOperation>();
  const { isHaveRole } = usePermissions();

  const [isOpenDeleteDialog, setIsOpenDeleteDialog] = useState(false);
  const [isOpenRejectDialog, setIsOpenRejectDialog] = useState(false);

  const ids = selectedIds?.map((id) => Number(getId(id))) || [];

  const isEverySelectedWithStatus = (allowedStatuses: BankOperationStatusesNew[]) =>
    !!selectedIds?.length &&
    selectedIds.every((id) => {
      const bankOperation = data?.find((obj) => obj.id === id);

      return bankOperation && allowedStatuses.includes(bankOperation.status?.value);
    });

  const isStatusCreatedRejected = useMemo(
    () =>
      isEverySelectedWithStatus([
        BankOperationStatusesNew.Created,
        BankOperationStatusesNew.Rejected,
      ]),
    [selectedIds, data],
  );

  const canBulkApproveReject = useMemo(
    () =>
      isEverySelectedWithStatus([BankOperationStatusesNew.OnApproval]) &&
      !isHaveRole([Role.Finance]),
    [selectedIds, data, isHaveRole],
  );

  const { bulkDeleteRequestsBankOps } = useBulkDeleteRequestsBankOps();
  const { bulkApproveRequestsBankOps } = useBulkApproveRequestsBankOps();
  const { bulkOnApprovalRequestsBankOps } = useBulkOnApprovalRequestsBankOps();

  const resource = useResourceContext();
  const unselectAll = useUnselectAll(resource);

  return (
    <BulkActionsToolbar selectAllButton={false}>
      {canBulkApproveReject && (
        <Button
          color="primary"
          label="Согласовать"
          onClick={() => {
            bulkApproveRequestsBankOps.mutate({ ids });
            unselectAll();
          }}
          startIcon={<CheckCircleOutlineIcon />}
        />
      )}
      {isStatusCreatedRejected && (
        <Button
          color="warning"
          label="На согласование"
          onClick={() => {
            bulkOnApprovalRequestsBankOps.mutate({ ids });
            unselectAll();
          }}
          startIcon={<ErrorOutlineIcon />}
        />
      )}
      {canBulkApproveReject && (
        <Button
          color="error"
          label="Отклонить"
          onClick={() => {
            setIsOpenRejectDialog(true);
          }}
          startIcon={<RemoveCircleOutlineIcon />}
        />
      )}
      {isStatusCreatedRejected && (
        <Button
          color="error"
          label="Удалить"
          onClick={() => setIsOpenDeleteDialog(true)}
          startIcon={<RemoveCircleOutlineIcon />}
        />
      )}
      <RequestsBankOpsListDatagridBulkRejectDialog
        ids={ids}
        onClose={() => setIsOpenRejectDialog(false)}
        onSuccess={() => {
          setIsOpenRejectDialog(false);
          unselectAll();
        }}
        open={isOpenRejectDialog}
      />
      <BaseDialog
        message={`Подтвердите удаление ${selectedIds?.length} ${selectedIds?.length === 1 ? 'заявки на банковскую операцию' : 'заявок на банковские операции'}. ${selectedIds?.length === 1 ? 'Заявка на банковскую операцию будет удалена' : 'Заявки на банковские операции будут удалены'} без возможности восстановления.`}
        onClose={() => setIsOpenDeleteDialog(false)}
        open={isOpenDeleteDialog}
        rightActionsSlot={[
          <CancelButton
            key="cancel-button"
            label="Отменить"
            onClick={() => setIsOpenDeleteDialog(false)}
            variant="outlined"
          />,
          <Button
            color="error"
            key="ok-button"
            label="Удалить"
            onClick={() => {
              bulkDeleteRequestsBankOps.mutate({ ids });
              unselectAll();
              setIsOpenDeleteDialog(false);
            }}
            variant="contained"
          />,
        ]}
        title={
          selectedIds?.length === 1
            ? 'Удаление заявки на банковскую операцию'
            : 'Удаление заявок на банковские операции'
        }
        width="640px"
      />
    </BulkActionsToolbar>
  );
};
